import {
  ArrowBackIosNew,
  FileDownloadOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  Cached,
  Search,
  Close,
  Circle
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Typography,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { axios_tenant } from "../../../../axios";
import React, { useEffect, useState } from "react";
import { format, isToday, isYesterday, parseISO } from "date-fns";

const STATUS_COLOR_MAPPING = {
  Success: "#24BC7C",
  Pending: "#FCD426",
  Failed: "#FC5C5C",
};

const IpAssistantTask = ({ setSwitchedComponent, filterData = "" }) => {
  const [expandedTask, setExpandedTask] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [taskData, setTaskData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [multiFilter, setMultiFilter] = useState({
    status: "",
    searchText: "",
  });

  const resetFilter = () => {
    setMultiFilter({
      status: "",
      searchText: "",
    });
  };

  const handleDialogOpen = (task) => {
    setSelectedTask(task);
  };

  const handleDialogClose = () => {
    setSelectedTask(null);
  };

  const getTasks = async () => {
    setLoading(true);
    const response = await axios_tenant.get("/trexo-assistant/getTasks");
    if (response.status === 200) {
      const groupedTasks = groupTasksByDate(response.data.ipAssistantTasks);
      setTaskData(groupedTasks);
    }
    const currentFilterData = filterData;
    setLoading(false);
    if (currentFilterData.length > 0) {
      setMultiFilter({
        status: "",
        searchText: currentFilterData,
      });
    }
  };

  useEffect(() => {
    getTasks();
  }, []);

  const refreshTasks = async () => {
    await getTasks();
    resetFilter();
  };

  const groupTasksByDate = (tasks) => {
    return tasks.reduce((acc, task) => {
      const date = parseISO(task.created_at); // Assumes timestamp is ISO format
      let label;

      if (isToday(date)) {
        label = "Today";
      } else if (isYesterday(date)) {
        label = "Yesterday";
      } else {
        label = format(date, "dd-MMM-yyyy");
      }

      if (!acc[label]) acc[label] = [];
      acc[label].push(task);
      return acc;
    }, {});
  };

  const handleExpandClick = (taskId) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  const getDateSortValue = (dateLabel) => {
    if (dateLabel === "Today") return 3;
    if (dateLabel === "Yesterday") return 2;
    return 1; // For all other dates
  };

  const handleSearchWithDebounce = (event) => {
    const { name, value } = event.target;
    setMultiFilter((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setMultiFilter((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  return (
    <Box
      component={"div"}
      sx={{
        flex: { xs: "1", xl: "0.8" },
        width: "100%",
        height: "100%",
        backgroundColor: "#f3f8fc",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "start",
        gap: 0.5,
        paddingX: { xs: "4rem", xl: "2rem" },
        borderRadius: "6px",
      }}
    >
      <Box
        component={"div"}
        sx={{
          width: "100%",
          maxWidth: "60rem",
          display: "flex",
          justifyContent: "start",
          flexDirection: "column",
          gap: 1.5,
          mb: 1.5,
          position: "relative",
        }}
      >
        <Box  sx={{ backgroundColor: "#f3f8fc", position: "fixed", maxWidth: "60rem", zIndex: 100 , width: "100%" , paddingBottom: "1rem"}}>
          <Box display={"flex"} alignContent={"center"} justifyContent={"space-between"}>
            <Button
              onClick={() => setSwitchedComponent(false)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                width: "fit-content",
              }}
            >
              <Typography
                fontSize={".9rem"}
                sx={{
                  cursor: "pointer",
                  color: "black",
                  letterSpacing: ".5px",
                  textTransform: "none",
                }}
                >
                <ArrowBackIosNew fontSize="small" /> Back to IP Assistant Chat
              </Typography>
            </Button>
            <IconButton disabled={loading}>
              <Cached onClick={refreshTasks} />
            </IconButton>
          </Box>
          <Box display={"flex"} alignContent={"center"} justifyContent={"space-between"} marginTop={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: ".5rem",
              flexGrow: 1,
            }}
          >
            <FormControl size="small" sx={{ width: "100%", maxWidth: "13rem" }}>
              <InputLabel>Select Status</InputLabel>
              <Select
                value={multiFilter.status}
                onChange={handleFilterChange}
                label="Select Status"
                name="status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <Button
              onClick={resetFilter}
              sx={{
                cursor: "pointer",
                color: "#005cb9",
                textTransform: "none",
                letterSpacing: "0.4px",
              }}
            >
              Reset
            </Button>
          </Box>
          <TextField
            name="searchText"
            value={multiFilter.searchText}
            onChange={handleSearchWithDebounce}
            placeholder="Search"
            size="small"
            sx={{
              maxWidth: "18rem",
              width: "100%",
              flexGrow: 1,
              backgroundColor: "white",
              borderRadius: "12rem",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12rem",
                "&.Mui-focused fieldset": {
                  borderColor: "#005cb9", // When focused
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {multiFilter.searchText.length > 0 ? (
                    <Close
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        handleSearchWithDebounce({
                          target: { name: "searchText", value: "" },
                        });
                      }}
                    />
                  ) : (
                    <Search />
                  )}
                </InputAdornment>
              ),
            }}
          />
          </Box>
        </Box>
        
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "8rem"
            }}
          >
            <CircularProgress fontSize="small" />
          </div>
        ) : (
          <div style={{marginTop: "7rem"}}>
            {Object.keys(taskData).length > 0 ? (
              (() => {
                const processedEntries = Object.entries(taskData)
                  .sort((a, b) => {
                    const labelA = a[0];
                    const labelB = b[0];
                    const valueA = getDateSortValue(labelA);
                    const valueB = getDateSortValue(labelB);
                    if (valueA !== valueB) return valueB - valueA;
                    if (valueA === 1 && valueB === 1) {
                      const dateA = parseISO(taskData[labelA][0].created_at);
                      const dateB = parseISO(taskData[labelB][0].created_at);
                      return dateB - dateA;
                    }
                    return 0;
                  })
                  .map(([date, tasks]) => {
                    const filteredTasks = tasks.filter((task) => {
                      const matchStatus =
                        multiFilter?.status === "" ||
                        multiFilter?.status === "all" ||
                        task?.status?.toLowerCase().trim() === multiFilter?.status;
                      const searchText =
                        multiFilter.searchText === "" ||
                        [
                          task?.request_id?.toString(),
                          task?.task_type,
                          task?.sub_task || "",
                          task?.status,
                        ].some((field) =>
                          field
                            ?.toLowerCase()
                            ?.includes(
                              multiFilter?.searchText?.trim()?.toLowerCase()
                            )
                        );

                      return matchStatus && searchText;
                    });
                    return { date, filteredTasks };
                  });

                const filteredEntries = processedEntries.filter(
                  (entry) => entry.filteredTasks.length > 0
                );

                if (filteredEntries.length === 0) {
                  return (
                    <Box sx={{ textAlign: "center", p: 3 }}>
                      <Typography variant="body2" sx={{ color: "gray" }}>
                        No tasks found
                      </Typography>
                    </Box>
                  );
                }

                return filteredEntries.map(({ date, filteredTasks }) => (
                  <div key={date}>
                    <Divider sx={{ marginTop: "1rem" }}>{date}</Divider>
                    {filteredTasks
                      .sort(
                        (a, b) =>
                          new Date(b.created_at) - new Date(a.created_at)
                      )
                      .map((task) => (
                        <Box
                          key={task.id}
                          component={"div"}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(12, 1fr)",
                            gap: 1.5,
                            width: "100%",
                            maxWidth: "60rem",
                            padding: 2,
                            marginTop: "1rem",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              textAlign: "start",
                              color: "gray",
                              gridColumn: "span 4",
                            }}
                          >
                            Task Type:{" "}
                            <span
                              style={{
                                color: "black",
                                marginLeft: "0.5rem",
                              }}
                            >
                              {task.task_type}
                            </span>
                          </Typography>
                          <Typography
                            sx={{
                              textAlign: "start",
                              color: "gray",
                              gridColumn: "span 3",
                            }}
                          >
                            Request ID:{" "}
                            <span
                              style={{
                                color: "black",
                                marginLeft: "0.5rem",
                              }}
                            >
                              {task.request_id}
                            </span>
                          </Typography>
                          <Typography
                            sx={{
                              textAlign: "start",
                              color: "gray",
                              gridColumn: "span 3",
                            }}
                          >
                            Status:{" "}
                            <span
                              style={{
                                color: STATUS_COLOR_MAPPING[task.status],
                                marginLeft: "0.5rem",
                              }}
                            >
                              {task.status}
                            </span>
                          </Typography>
                          
                          <Typography sx={{ gridColumn: "span 1" }}>
                            {
                              (task.status === "Success" && task.download_link)  && (
                                <a
                                  href={task.download_link}
                                  style={{
                                    textDecoration: "none",
                                    color: "#005cb9",
                                  }}
                                >
                                  <FileDownloadOutlined fontSize="small" />
                                </a>
                              ) 
                              // : task.status === "Failed" ? (
                              //   <Replay
                              //     onClick={() => handleRetry(task.request_id)}
                              //     fontSize="small"
                              //     sx={{ cursor: "pointer", color: "#005cb9" }}
                              //   />
                              // ) : null
                            }
                          </Typography>

                          {expandedTask === task.id ? (
                            <KeyboardArrowUpOutlined
                              fontSize="small"
                              sx={{
                                cursor: "pointer",
                                color: "black",
                                textAlign: "center",
                                gridColumn: "span 1",
                              }}
                              onClick={() => handleExpandClick(task.id)}
                            />
                          ) : (
                            <KeyboardArrowDownOutlined
                              fontSize="small"
                              sx={{
                                cursor: "pointer",
                                color: "black",
                                textAlign: "center",
                                gridColumn: "span 1",
                              }}
                              onClick={() => handleExpandClick(task.id)}
                            />
                          )}
                          <Collapse
                            in={expandedTask === task.id}
                            sx={{
                              gridColumn: "span 12",
                              mt: 1.5,
                              display:
                                expandedTask === task.id ? "block" : "none",
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(12, 1fr)",
                                gap: 1.5,
                                width: "100%",
                              }}
                            >
                              <Typography
                                sx={{
                                  textAlign: "start",
                                  color: "gray",
                                  gridColumn: "span 4",
                                }}
                              >
                                Request Time:{" "}
                                <span
                                  style={{
                                    color: "black",
                                    marginLeft: "0.5rem",
                                  }}
                                >
                                  {format(
                                    parseISO(task.created_at),
                                    "dd-MMM-yyyy, h:mm a"
                                  )}
                                </span>
                              </Typography>
                              {task.status !== "Pending" ? (
                                <Typography
                                  sx={{
                                    textAlign: "start",
                                    color: "gray",
                                    gridColumn: "span 3",
                                  }}
                                >
                                  Completion Time:{" "}
                                  <span
                                    style={{
                                      color: "black",
                                      marginLeft: "0.5rem",
                                    }}
                                  >
                                    {format(
                                      parseISO(task.updated_at),
                                      "dd-MMM-yyyy, h:mm a"
                                    )}
                                  </span>
                                </Typography>
                              ) : null}
                              <Typography
                                sx={{
                                  textAlign: "start",
                                  color: "gray",
                                  gridColumn: "span 3",
                                }}
                              >
                                Subtask:{" "}
                                <span
                                  style={{
                                    color: "black",
                                    marginLeft: "0.5rem",
                                  }}
                                >
                                  {task.sub_task ?? "N/A"}
                                </span>
                              </Typography>
                              {(task.sub_task?.includes("SB-08") || task.task_type === "AppGen"|| task.task_type ==="OA Shell Draft") && 
                              (
                                <Typography
                                  sx={{
                                    textAlign: "start",
                                    color: "gray",
                                    gridColumn: "span 12",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  IP References:{" "}
                                  <span
                                    style={{
                                      color: "black",
                                      marginLeft: "0.5rem",
                                    }}
                                  >
                                    {(task.ip_reference?.applicationNumber || task.ip_reference?.docket_number) ?? "N/A"}
                                  </span>
                                </Typography>
                              )}
                              {task.error_message && (
                                <>
                                  <Typography
                                    sx={{
                                      textAlign: "start",
                                      color: "gray",
                                      gridColumn: "span 12",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    Error Message:{" "}
                                    <span
                                      style={{
                                        color: "black",
                                        marginLeft: "0.5rem",
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {task.error_message.split(";")[0].trim()}
                                    </span>
                                    {task.error_message.split(";").length >
                                      1 && (
                                      <span
                                        onClick={() => handleDialogOpen(task)}
                                        style={{
                                          marginLeft: "0.5rem",
                                          cursor: "pointer",
                                          color: "#005cb9",
                                          textDecoration: "underline",
                                        }}
                                      >
                                        View More Errors
                                      </span>
                                    )}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                  </div>
                ));
              })()
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  mt: 4,
                }}
              >
                <Typography variant="body2" sx={{ color: "gray" }}>
                  No tasks found
                </Typography>
              </Box>
            )}
          </div>
        )}
      </Box>
      <Modal
        open={!!selectedTask}
        onClose={handleDialogClose}
        closeAfterTransition
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "30%",
            maxHeight: "80%",
            my: "auto",
            mx: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            overflowY: "auto",
          }}
        >
          {selectedTask?.error_message.split(";").map((error, index) => (
            <Typography key={index} sx={{ overflowWrap: "anywhere", mb: 1.5 }}>{error.trim()}</Typography>
          ))}
        </Box>
      </Modal>
    </Box>
  );
};

export default IpAssistantTask;
